import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SERVICE_TYPES } from "@/lib/calc";
import { api } from "@/lib/api";
import { toast } from "sonner";

const empty = {
  name: "",
  monthly_fee: "",
  included_hours: "",
  hourly_cost: "",
  logged_hours: "0",
  service_type: "",
  owner: "",
  notes: "",
  currency: "",
};

const CURRENCIES = [
  { code: "USD", label: "USD · US Dollar ($)" },
  { code: "CAD", label: "CAD · Canadian Dollar (C$)" },
  { code: "GBP", label: "GBP · British Pound (£)" },
  { code: "AUD", label: "AUD · Australian Dollar (A$)" },
  { code: "NZD", label: "NZD · New Zealand Dollar (NZ$)" },
  { code: "EUR", label: "EUR · Euro (€)" },
];

const Field = ({ label, hint, children, testid }) => (
  <div className="flex flex-col gap-1.5" data-testid={testid}>
    <label className="text-xs font-semibold text-[#1F2326] tracking-tight">{label}</label>
    {children}
    {hint ? <div className="text-[11px] text-[#69747C]">{hint}</div> : null}
  </div>
);

const ClientFormDialog = ({ open, onOpenChange, client, onSaved }) => {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (client) {
      setForm({
        name: client.name || "",
        monthly_fee: String(client.monthly_fee ?? ""),
        included_hours: String(client.included_hours ?? ""),
        hourly_cost: String(client.hourly_cost ?? ""),
        logged_hours: String(client.logged_hours ?? "0"),
        service_type: client.service_type || "",
        owner: client.owner || "",
        notes: client.notes || "",
        currency: client.currency || "",
      });
    } else {
      setForm(empty);
    }
  }, [client, open]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const inputCls =
    "w-full bg-white border border-[#E4DFD2] rounded-lg px-3 py-2 text-sm text-[#1F2326] placeholder-[#A9A294] focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326] transition-colors";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    const payload = {
      name: form.name.trim(),
      monthly_fee: parseFloat(form.monthly_fee) || 0,
      included_hours: parseFloat(form.included_hours) || 0,
      hourly_cost: parseFloat(form.hourly_cost) || 0,
      logged_hours: parseFloat(form.logged_hours) || 0,
      service_type: form.service_type || "",
      owner: form.owner || "",
      notes: form.notes || "",
      currency: form.currency || null,
    };
    try {
      setSaving(true);
      if (client?.id) {
        await api.put(`/clients/${client.id}`, payload);
        toast.success(`${payload.name} updated`);
      } else {
        const r = await api.post("/clients", payload);
        toast.success(`${payload.name} added`);
        if (r?.data?.samples_cleared) {
          toast.info(
            `Sample retainers removed — you've got the real thing now.`,
            { duration: 6000 }
          );
        }
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[580px] bg-white border-[#E4DFD2] rounded-xl"
        data-testid="client-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight text-[#1F2326]">
            {client?.id ? "Edit retainer" : "Add a retainer"}
          </DialogTitle>
          <DialogDescription className="text-[#69747C]">
            Just the basics. Status updates as numbers change.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <Field label="Client name" testid="field-name">
            <input
              data-testid="input-client-name"
              className={inputCls}
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="Brightline Studio"
              required
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Monthly retainer fee" hint="Gross billed per month" testid="field-fee">
              <input
                data-testid="input-monthly-fee"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                className={inputCls}
                value={form.monthly_fee}
                onChange={(e) => setField("monthly_fee", e.target.value)}
                placeholder="3000"
                required
              />
            </Field>
            <Field label="Included monthly hours" testid="field-included">
              <input
                data-testid="input-included-hours"
                type="number"
                step="0.5"
                min="0"
                inputMode="decimal"
                className={inputCls}
                value={form.included_hours}
                onChange={(e) => setField("included_hours", e.target.value)}
                placeholder="30"
                required
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Internal hourly cost"
              hint="Fully-loaded cost per hour"
              testid="field-cost"
            >
              <input
                data-testid="input-hourly-cost"
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                className={inputCls}
                value={form.hourly_cost}
                onChange={(e) => setField("hourly_cost", e.target.value)}
                placeholder="50"
                required
              />
            </Field>
            <Field label="Logged hours this month" testid="field-logged">
              <input
                data-testid="input-logged-hours"
                type="number"
                step="0.25"
                min="0"
                inputMode="decimal"
                className={inputCls}
                value={form.logged_hours}
                onChange={(e) => setField("logged_hours", e.target.value)}
                placeholder="0"
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Service type" testid="field-service-type">
              <Select
                value={form.service_type || ""}
                onValueChange={(v) => setField("service_type", v)}
              >
                <SelectTrigger
                  data-testid="input-service-type"
                  className="w-full bg-white border-[#E4DFD2] rounded-lg text-sm"
                >
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {SERVICE_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Owner (optional)" testid="field-owner">
              <input
                data-testid="input-owner"
                className={inputCls}
                value={form.owner}
                onChange={(e) => setField("owner", e.target.value)}
                placeholder="Account owner"
              />
            </Field>
          </div>

          <Field label="Currency (optional)" hint="Leave blank to use your workspace default." testid="field-currency">
            <Select
              value={form.currency || "__default__"}
              onValueChange={(v) => setField("currency", v === "__default__" ? "" : v)}
            >
              <SelectTrigger
                data-testid="input-currency"
                className="w-full bg-white border-[#E4DFD2] rounded-lg text-sm"
              >
                <SelectValue placeholder="Use workspace default" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="__default__">Use workspace default</SelectItem>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field label="Notes (optional)" testid="field-notes">
            <textarea
              data-testid="input-notes"
              className={`${inputCls} min-h-[80px] resize-y`}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Scope, account owner, anything to remember."
            />
          </Field>

          <DialogFooter className="pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              data-testid="form-cancel-btn"
              className="bg-white text-[#1F2326] border border-[#E4DFD2] hover:bg-[#F3EFE3] px-4 py-2 rounded-lg font-medium text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="form-submit-btn"
              className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] disabled:opacity-60 px-4 py-2 rounded-lg font-medium text-sm transition-colors inline-flex items-center justify-center"
            >
              {saving ? "Saving..." : client?.id ? "Save changes" : "Add retainer"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ClientFormDialog;
